import React from "react";
import { Grid, Typography, Input, Button, Radio, Toggle } from "@sumprio/ui";
import { EyeIcon, EyeSlashIcon } from "@sumprio/ui/icons";
import { RequiredFieldLabel } from "../RequiredFieldLabel";
import { flipBoolean } from "utils/cases/toggleBizViewOnly";
import type { DocumentAccessSettingsState } from "./types";

export interface DocumentAccessSettingsProps {
  value: DocumentAccessSettingsState;
  onChange: (_next: DocumentAccessSettingsState) => void;
  /** Unique radio group name (required when multiple instances share a page). */
  passwordRadioName: string;
  showClientVisibility?: boolean;
  disabled?: boolean;
  clientVisibilityLabel?: string;
}

export function DocumentAccessSettings({
  value,
  onChange,
  passwordRadioName,
  showClientVisibility = true,
  disabled = false,
  clientVisibilityLabel = "Allow the client to view this document.",
}: DocumentAccessSettingsProps) {
  const patch = (partial: Partial<DocumentAccessSettingsState>) =>
    onChange({ ...value, ...partial });

  return (
    <Grid display="flex" flexDirection="column" gap="md" width="100%">
      <Grid display="flex" flexDirection="column" gap="sm">
        <RequiredFieldLabel label="Is the document password protected?" />
        <Grid display="flex" flexWrap="wrap" gap="md" marginTop="xs">
          <Radio
            name={passwordRadioName}
            value="yes"
            label="Yes"
            checked={value.isProtected === true}
            onChange={() => patch({ isProtected: true })}
            size="sm"
            disabled={disabled}
          />
          <Radio
            name={passwordRadioName}
            value="no"
            label="No"
            checked={value.isProtected === false}
            onChange={() => patch({ isProtected: false, password: "", showPassword: false })}
            size="sm"
            disabled={disabled}
          />
        </Grid>
        {value.isProtected ? (
          <Input
            size="sm"
            label="Enter Password"
            required
            placeholder="Enter Password"
            type={value.showPassword ? "text" : "password"}
            value={value.password}
            disabled={disabled}
            onChange={(e) => patch({ password: (e.target as HTMLInputElement).value })}
            width="full"
            trailingElement={
              <Button
                hoverEffect={false}
                iconOnly
                icon={value.showPassword ? EyeSlashIcon : EyeIcon}
                variant="brand-tertiaryColor"
                size="sm"
                disabled={disabled}
                aria-label={value.showPassword ? "Hide password" : "Show password"}
                onClick={() => patch({ showPassword: !value.showPassword })}
              />
            }
          />
        ) : null}
      </Grid>
      {showClientVisibility ? (
        <Grid display="flex" alignItems="center" justifyContent="space-between" gap="md">
          <Typography variant="body" bodySize="sm" bodyWeight="regular">
            {clientVisibilityLabel}
          </Typography>
          <Toggle
            size="sm"
            checked={value.clientCanView}
            disabled={disabled}
            onChange={() => patch({ clientCanView: flipBoolean(value.clientCanView) })}
            aria-label={clientVisibilityLabel}
          />
        </Grid>
      ) : null}
    </Grid>
  );
}
